/**
 * Native authentication strategy implementation
 * Handles authentication changes in native mobile applications
 */

import { IAuthStrategy } from './IAuthStrategy';
import { IEventService } from '../../../../services/eventService';
import { INavigationService } from '../../../../services/navigationService';
import { IContainer } from '../../../../core/containers';
import { ITenantHandlerService } from '../../../../services/tenantHandler';
import { ISessionService } from '../../../../services/session';
import { InitialNavigationStateRepository } from '../InitialNavigationStateRepository';
import { internalLogger } from '../../../../interface/v1/clientOsLogger';
import { LOGIN_PATHNAME } from '../../../../services/session/constants';
import bindAllMethods from '../../../../utils/bindAllMethods';
import InitializationTokenCache from '../../../../services/JWeb/cache/InitializationTokenCache';
import { clearRepositories } from '../../../../infra/repositories/clearRepositories';
import { IUserOnboardingService } from '../../../../services/userOnboardingService';

/**
 * Event name for native Auth plugin login change events
 */
const AUTH_PLUGIN_LOGIN_CHANGE_EVENT_NAME =
  'com.hp.jarvis.auth.event.login.change';

/**
 * Interface defining the required shape of authentication event data
 */
export interface IAuthEventData {
  currentAccount?: {
    emailAddress?: string;
    tenantId?: string;
  };
  previousAccount?: {
    emailAddress?: string;
    tenantId?: string;
  };
}

/**
 * Configuration parameters for native authentication strategy
 */
export interface INativeAuthStrategyParams {
  authRefreshContainer: IContainer;
  eventService: IEventService;
  navigationService: INavigationService;
  initialNavigationStateRepository: InitialNavigationStateRepository;
  tenantHandlerService: ITenantHandlerService;
  sessionService: ISessionService;
  userOnboardingService: IUserOnboardingService;
  refreshScreenOnChange: boolean;
  nativeRefreshServicesOnAuthChange: boolean;
}

/**
 * Native authentication strategy implementation
 * Follows Single Responsibility Principle by focusing solely on native auth handling
 */
export class NativeAuthStrategy implements IAuthStrategy {
  private eventService: IEventService;
  private navigationService: INavigationService;
  private tenantHandlerService: ITenantHandlerService;
  private sessionService: ISessionService;
  private userOnboardingService: IUserOnboardingService;
  private authRefreshContainer: IContainer;
  private initialNavigationStateRepository: InitialNavigationStateRepository;
  private refreshScreenOnChange: boolean;
  private nativeRefreshServicesOnAuthChange: boolean;

  constructor({
    authRefreshContainer,
    eventService,
    navigationService,
    initialNavigationStateRepository,
    tenantHandlerService,
    sessionService,
    userOnboardingService,
    refreshScreenOnChange,
    nativeRefreshServicesOnAuthChange
  }: INativeAuthStrategyParams) {
    bindAllMethods(this);

    this.authRefreshContainer = authRefreshContainer;
    this.eventService = eventService;
    this.navigationService = navigationService;
    this.initialNavigationStateRepository = initialNavigationStateRepository;
    this.tenantHandlerService = tenantHandlerService;
    this.sessionService = sessionService;
    this.userOnboardingService = userOnboardingService;
    this.refreshScreenOnChange = refreshScreenOnChange;
    this.nativeRefreshServicesOnAuthChange = nativeRefreshServicesOnAuthChange;
  }

  /**
   * Initializes the native authentication strategy
   */
  public async init(): Promise<void> {
    // Skip subscription if refresh listener is disabled in manifest
    if (!this.refreshScreenOnChange) {
      /* istanbul ignore next */
      internalLogger?.debug?.(
        'Native refresh listener is disabled, not subscribing to auth change events.'
      );
      return;
    }

    // Apply any saved navigation state from previous session
    this.consumeSavedNavigationState();

    // Subscribe to authentication change events
    await this.eventService.subscribe(
      AUTH_PLUGIN_LOGIN_CHANGE_EVENT_NAME,
      this.handleAuthChangeEvent
    );

    if (this.sessionService.isLoggedIn()) {
      /* istanbul ignore next */
      internalLogger?.debug?.d4?.({
        className: 'NativeAuthStrategy',
        methodName: 'init()',
        message: 'Rehydrating InitializationTokenCache in NativeAuthStrategy.',
        variables: {}
      });
      await InitializationTokenCache.rehydrateToken();
    }
  }

  /**
   * Handles authentication status changes for native applications
   */
  public async handleAuthStatusChange(): Promise<void> {
    if (this.nativeRefreshServicesOnAuthChange) {
      // New behavior: Refresh container context without page reload
      internalLogger?.debug?.(
        'Native refresh listener is enabled, refreshing auth container.'
      );
      await this.authRefreshContainer.refreshContext();
    } else {
      // Legacy behavior: Reload page while preserving state
      internalLogger?.debug?.(
        'Native refresh listener is disabled, reloading page while keeping state.'
      );
      this.reloadPageKeepingState();
    }
  }

  /**
   * Handles tenant changes for native applications
   */
  public handleTenantChange(oldTenant?: string, newTenant?: string): void {
    const hasTenantChanged = newTenant !== oldTenant;
    if (newTenant && hasTenantChanged) {
      void this.tenantHandlerService.setTenant(
        newTenant,
        this.tenantHandlerService.START_TENANT_LEVEL,
        { reload: false }
      );
    }
  }

  /**
   * Handles authentication change events from native platform
   */
  private handleAuthChangeEvent = async (event: {
    eventData?: IAuthEventData;
  }): Promise<void> => {
    const { eventData } = event || {};
    const isLoggedIn = this.sessionService.isLoggedIn();
    const currentPath = this.navigationService.location.pathname;

    const {
      hasUserLoggedInOrLoggedoutFromJWeb,
      hasUserLoggedInOrLoggedoutFromShell,
      hasUserChanged
    } = this.checkAuthStatus(isLoggedIn, eventData);

    const { previousAccount, currentAccount } = eventData || {};
    const oldTenant = previousAccount?.tenantId;
    const newTenant = currentAccount?.tenantId;

    // Handle orgless state transition
    if (previousAccount && currentAccount && !currentAccount.tenantId) {
      await this.handleOrglessState();
      return;
    }

    // Handle login/logout scenarios when not on login page
    if (
      currentPath !== LOGIN_PATHNAME &&
      (hasUserLoggedInOrLoggedoutFromJWeb ||
        hasUserLoggedInOrLoggedoutFromShell ||
        hasUserChanged)
    ) {
      if (hasUserChanged) {
        clearRepositories();
      }
      await this.handleAuthStatusChange();
      return;
    }

    // Handle tenant change scenarios
    this.handleTenantChange(oldTenant, newTenant);
  };

  /**
   * Reloads the page while preserving the current navigation state
   */
  private reloadPageKeepingState(): void {
    const currentState = this.navigationService.location.state;
    const currentPath = this.navigationService.location.pathname;
    this.initialNavigationStateRepository.save(currentPath, currentState);
    const url = new URL(window.location.href);
    //Prevent browser cache from serving stale JavaScript after auth changes
    url.searchParams.set('_t', Date.now().toString());
    window.location.href = url.toString();
  }

  /**
   * Removes the cache-busting timestamp parameter from URL
   */
  private cleanupCacheBustingParameter(): void {
    const searchParams = new URLSearchParams(
      this.navigationService.location.search
    );
    if (searchParams.has('_t')) {
      searchParams.delete('_t');
      this.navigationService.replace({
        ...this.navigationService.location,
        search: searchParams.toString()
      });
    }
  }

  /**
   * Applies any saved navigation state from previous sessions
   * TODO: This should be a external navigation utility function
   */
  private consumeSavedNavigationState(): void {
    // Clean up cache-busting parameter if present to avoid polluting navigation
    this.cleanupCacheBustingParameter();
    const currentPath = this.navigationService.location.pathname;
    const savedState =
      this.initialNavigationStateRepository.findOne(currentPath);

    if (savedState) {
      this.initialNavigationStateRepository.clear();
      this.navigationService.replace({
        ...this.navigationService.location,
        state: savedState
      });
    }
  }

  /**
   * Checks authentication status based on event data
   */
  private checkAuthStatus(
    isLoggedIn: boolean,
    eventData?: IAuthEventData
  ): {
    hasUserLoggedInOrLoggedoutFromJWeb: boolean;
    hasUserLoggedInOrLoggedoutFromShell: boolean;
    hasUserChanged: boolean;
  } {
    const currentEmail = eventData?.currentAccount?.emailAddress;
    const previousEmail = eventData?.previousAccount?.emailAddress;

    const hasCurrentEmail = !!currentEmail;
    const hasPreviousEmail = !!previousEmail;

    const hasUserLoggedInOrLoggedoutFromJWeb =
      !hasCurrentEmail || !hasPreviousEmail;

    const hasUserLoggedInOrLoggedoutFromShell =
      (!isLoggedIn && hasCurrentEmail) || (isLoggedIn && !hasCurrentEmail);

    const hasUserChanged =
      hasCurrentEmail && hasPreviousEmail && currentEmail !== previousEmail;

    return {
      hasUserLoggedInOrLoggedoutFromJWeb,
      hasUserLoggedInOrLoggedoutFromShell,
      hasUserChanged
    };
  }

  /**
   * Exchanging from an org-aware state to an orgless state is invalid
   * Redirects to onboarding path if available, otherwise logs out
   */
  private async handleOrglessState(): Promise<void> {
    internalLogger?.debug?.({
      className: 'NativeAuthStrategy',
      methodName: 'handleOrglessState',
      message: 'User transitioned to orgless state'
    });

    const userOnboardingPath = this.userOnboardingService.getOnboardingPath();

    if (userOnboardingPath) {
      internalLogger?.debug?.({
        className: 'NativeAuthStrategy',
        methodName: 'handleOrglessState',
        message: `Redirecting to onboarding path: ${userOnboardingPath}`
      });
      this.navigationService.redirect(userOnboardingPath);
    } else {
      internalLogger?.debug?.({
        className: 'NativeAuthStrategy',
        methodName: 'handleOrglessState',
        message: 'No onboarding path configured, logging out user'
      });
      await this.sessionService.logout();
    }
  }
}
